import { Toast as BaseToast } from '@base-ui/react'
import { clsx } from 'clsx'
import type { ReactNode } from 'react'

export type ToastVariant = 'error' | 'success' | 'warning' | 'information'

export interface ToastRootProps {
  variant: ToastVariant
  toast: React.ComponentPropsWithoutRef<typeof BaseToast.Root>['toast']
  children: ReactNode
  className?: string
}

const TOAST_VARIANTS = {
  error: {
    container: 'border-brick-light bg-peach-25 text-brick-light',
    icon: 'fa-solid fa-triangle-exclamation text-brick-light',
    closeIcon: 'text-brick-light hover:text-brick',
    title: 'Error',
  },
  success: {
    container: 'border-forest bg-mint-25 text-forest',
    icon: 'fa-solid fa-circle-check text-forest',
    closeIcon: 'text-forest hover:text-forest-light',
    title: 'Success',
  },
  warning: {
    container: 'border-taupe-dark bg-gold-25 text-taupe-dark',
    icon: 'fa-solid fa-triangle-exclamation text-taupe-dark',
    closeIcon: 'text-taupe-dark hover:text-taupe',
    title: 'Warning',
  },
  information: {
    container: 'border-indigo bg-sky-50',
    icon: 'fa-solid fa-circle-info text-indigo',
    closeIcon: 'text-indigo hover:text-indigo-light',
    title: 'Information',
  },
}

export function Root({ variant, toast, className, children }: ToastRootProps) {
  return (
    <BaseToast.Root
      toast={toast}
      swipeDirection={['down', 'right']}
      role="alert"
      className={clsx(
        'w-[25rem] max-w-[calc(100vw-2rem)] p-4 flex items-start gap-2',
        'rounded border shadow-[0_4px_24px_0_rgba(0,0,0,0.12)]',
        TOAST_VARIANTS[variant].container,
        className
      )}
    >
      {children}
    </BaseToast.Root>
  )
}

export function Icon({ variant }: { variant: ToastVariant }) {
  return (
    <div className="flex py-1 justify-center items-center">
      <i className={TOAST_VARIANTS[variant].icon} />
    </div>
  )
}

export function Content({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof BaseToast.Content>) {
  return (
    <BaseToast.Content
      className={clsx('flex flex-col justify-center items-start flex-1', className)}
      {...props}
    >
      {children}
    </BaseToast.Content>
  )
}

export function Title({
  variant,
  className,
  ...props
}: { variant: ToastVariant } & React.ComponentPropsWithoutRef<typeof BaseToast.Title>) {
  return (
    <BaseToast.Title
      className={clsx('font-inter text-base font-semibold leading-[150%]', className)}
      {...props}
    >
      {TOAST_VARIANTS[variant].title}
    </BaseToast.Title>
  )
}

export function Description({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof BaseToast.Description>) {
  return (
    <BaseToast.Description
      className={clsx('font-inter text-base font-normal leading-[150%]', className)}
      {...props}
    />
  )
}

export function Link({
  href,
  children,
  className,
  onClick,
  ...props
}: React.ComponentPropsWithoutRef<'a'> & {
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (href === '#' || !href) {
      e.preventDefault()
    }
    onClick?.(e)
  }

  return (
    <a
      href={href}
      onClick={handleClick}
      className={clsx(
        'font-inter text-base font-normal leading-[150%] underline cursor-pointer hover:opacity-80',
        className
      )}
      {...props}
    >
      {children}
    </a>
  )
}

export function Close({
  variant,
  className,
  onClick,
  ...props
}: React.ComponentPropsWithoutRef<typeof BaseToast.Close> & {
  variant: ToastVariant
  onClick?: () => void
}) {
  return (
    <BaseToast.Close
      className={clsx(
        'flex justify-center items-center cursor-pointer',
        TOAST_VARIANTS[variant].closeIcon,
        className
      )}
      onClick={onClick}
      {...props}
    >
      <i className="fa-solid fa-xmark" />
    </BaseToast.Close>
  )
}
